import { Controller } from "@hotwired/stimulus"

// The feedback survey's section index (docs/07, the design's
// `pages/exhibitor-feedback/`).
//
// A form this long needs somewhere to say where you are. The rail highlights
// the section on screen and fills a bar as the page scrolls — the only two
// things the design's rail does, and both are about orientation rather than
// about the answers.
//
// It is an IntersectionObserver rather than a scroll handler: a scroll handler
// fires on every frame and has to be told the geometry of every section, which
// goes wrong the moment a validation error makes one of them taller.
export default class extends Controller {
  static targets = ["section", "link", "bar", "progress"]

  connect() {
    this.onScroll = this.updateBar.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })

    // The top third of the viewport: the section a person is reading is the
    // one whose heading has just passed under the top of the screen, not the
    // one in the middle.
    this.observer = new IntersectionObserver(this.highlight.bind(this), {
      rootMargin: "0px 0px -66% 0px", threshold: 0
    })
    this.sectionTargets.forEach((section) => this.observer.observe(section))

    this.updateBar()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    this.observer?.disconnect()
  }

  highlight(entries) {
    const visible = entries.find((entry) => entry.isIntersecting)
    if (!visible) return

    this.linkTargets.forEach((link) => {
      if (link.dataset.section === visible.target.id) link.setAttribute("aria-current", "true")
      else link.removeAttribute("aria-current")
    })
  }

  updateBar() {
    if (!this.hasBarTarget) return

    const scrollable = document.documentElement.scrollHeight - window.innerHeight
    // A page shorter than the window has nothing to scroll, and dividing by
    // zero would put the bar at NaN%.
    const percent = scrollable > 0 ? Math.min(100, Math.round((window.scrollY / scrollable) * 100)) : 0

    this.barTarget.style.width = `${percent}%`
    if (this.hasProgressTarget) this.progressTarget.setAttribute("aria-valuenow", String(percent))
  }
}
